import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import VoiceControls from './VoiceControls';
import ConversationPanel from './ConversationPanel';
import AudioVisualizer from './AudioVisualizer';
import { useVoiceWebSocket } from '../../hooks/voice-demo/useVoiceWebSocket';
import { useAudioPipeline } from '../../hooks/voice-demo/useAudioPipeline';
import { useConversationsQuery } from '../../hooks/voice-demo/useConversationsQuery';
import { useDeleteConversationMutation } from '../../hooks/voice-demo/useDeleteConversationMutation';
import { useSendMessageMutation } from '../../hooks/voice-demo/useSendMessageMutation';
import { useStreamMessage } from '../../hooks/voice-demo/useStreamMessage';
import type { PartnerUser } from '../../hooks/requests/queries/use_all_users_query';
import type { ConversationMessage } from '../../types/voice-demo';
import Alert from '../ui/Alert';

interface VoicePanelProps {
  selectedUser: PartnerUser;
  conversationId: string | null;
  onDeleted?: () => void;
}

const VoicePanel: React.FC<VoicePanelProps> = ({ selectedUser, conversationId, onDeleted }) => {
  const queryClient = useQueryClient();
  const voiceWs = useVoiceWebSocket();
  const audioPipeline = useAudioPipeline();
  const deleteConversation = useDeleteConversationMutation();
  const sendMessage = useSendMessageMutation();
  const { streamMessage, cancel: cancelStream, isStreaming } = useStreamMessage();
  const [micError, setMicError] = React.useState<string | null>(null);
  const [chatMessages, setChatMessages] = React.useState<ConversationMessage[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [useStreaming, setUseStreaming] = useState(true);
  const [streamError, setStreamError] = useState<string | null>(null);
  const prevStatusRef = useRef(voiceWs.status);

  // Invalidate conversations query when the session ends
  useEffect(() => {
    const prev = prevStatusRef.current;
    const curr = voiceWs.status;
    prevStatusRef.current = curr;

    const wasActive = prev === 'connected' || prev === 'recording' || prev === 'connecting';
    if (wasActive && curr === 'disconnected') {
      void queryClient.invalidateQueries({
        queryKey: ['partner', 'users', selectedUser.user_id, 'conversations'],
      });
    }
  }, [voiceWs.status, queryClient, selectedUser.user_id]);

  const isActive = voiceWs.status !== 'disconnected';

  // Fetch existing conversation messages
  const { data: existingConversation } = useConversationsQuery(
    selectedUser.user_id,
    selectedUser.company_id,
    conversationId || undefined
  );

  // Combine messages: use voiceWs.messages only while active (once disconnected,
  // the API query already contains the persisted voice messages)
  const existingMessages = existingConversation?.messages || [];
  const allMessages = [...existingMessages, ...(isActive ? voiceWs.messages : []), ...chatMessages];

  const handleConnect = useCallback(async () => {
    setMicError(null);
    setChatMessages([]);

    try {
      await audioPipeline.ensureAudioContext();

      await voiceWs.connect(
        selectedUser.user_id,
        selectedUser.company_id,
        conversationId,
        // onReady
        async () => {
          try {
            await audioPipeline.startRecording((audioData) => {
              voiceWs.sendAudioData(audioData);
            });
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to start microphone';
            setMicError(msg);
          }
        },
        // onAudioData
        async (data) => {
          await audioPipeline.playAudio(data);
        },
        // onInterruption
        () => {
          audioPipeline.stopAllPlayback();
        }
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to connect';
      setMicError(msg);
    }
  }, [selectedUser, conversationId, voiceWs, audioPipeline]);

  const handleDisconnect = useCallback(() => {
    voiceWs.disconnect();
    audioPipeline.stopRecording();
  }, [voiceWs, audioPipeline]);

  const handleDeleteConversation = useCallback(async () => {
    const convId = existingConversation?.conversationId;
    if (!convId) return;
    try {
      await deleteConversation.mutateAsync({
        userId: selectedUser.user_id,
        companyId: selectedUser.company_id,
        conversationId: convId,
      });
      setChatMessages([]);
      onDeleted?.();
    } catch {
      // mutation error handled by mutation state
    }
  }, [existingConversation?.conversationId, deleteConversation, selectedUser, onDeleted]);

  const handleSendMessage = useCallback(
    async (message: string) => {
      setStreamError(null);

      // Optimistically add the user message
      const userMsg: ConversationMessage = {
        role: 'human',
        content: message,
        created_at: new Date().toISOString(),
      };
      setChatMessages((prev) => [...prev, userMsg]);

      if (useStreaming) {
        // SSE path — accumulate chunks locally, finalize on `done`.
        let accumulated = '';
        setStreamingText('');
        await streamMessage(
          {
            userId: selectedUser.user_id,
            companyId: selectedUser.company_id,
            message,
            conversationId: existingConversation?.conversationId,
          },
          {
            onChunk: (text) => {
              accumulated += text;
              setStreamingText(accumulated);
            },
            onDone: () => {
              const allyMsg: ConversationMessage = {
                role: 'ai',
                content: accumulated,
                created_at: new Date().toISOString(),
              };
              setChatMessages((prev) => [...prev, allyMsg]);
              setStreamingText('');
            },
            onError: (err) => {
              setStreamError(err.message);
              setStreamingText('');
              setChatMessages((prev) => prev.filter((m) => m !== userMsg));
            },
          }
        );
        return;
      }

      // Non-streaming path — single JSON response.
      try {
        const result = await sendMessage.mutateAsync({
          userId: selectedUser.user_id,
          companyId: selectedUser.company_id,
          message,
          conversationId: existingConversation?.conversationId,
        });

        const allyMsg: ConversationMessage = {
          role: 'ai',
          content: result.response,
          created_at: new Date().toISOString(),
        };
        setChatMessages((prev) => [...prev, allyMsg]);
      } catch {
        setChatMessages((prev) => prev.filter((m) => m !== userMsg));
      }
    },
    [selectedUser, existingConversation?.conversationId, sendMessage, streamMessage, useStreaming]
  );

  // Cleanup on unmount or user/conversation change
  useEffect(() => () => {
      voiceWs.disconnect();
      audioPipeline.cleanup();
      cancelStream();
    }, [selectedUser.user_id, conversationId]);

  return (
    <div className="au-card p-6">
      {micError && (
        <Alert variant="warning" dismissible onDismiss={() => setMicError(null)} className="mb-4">
          {micError}
        </Alert>
      )}

      {streamError && (
        <Alert variant="danger" dismissible onDismiss={() => setStreamError(null)} className="mb-4">
          {streamError}
        </Alert>
      )}

      <VoiceControls
        status={voiceWs.status}
        isMuted={audioPipeline.isMuted}
        selectedUser={selectedUser}
        hasConversation={Boolean(existingConversation?.conversationId)}
        isDeletingConversation={deleteConversation.isPending}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        onToggleMute={audioPipeline.toggleMute}
        onDeleteConversation={handleDeleteConversation}
      />

      {isActive ? (
        <div className="flex items-center justify-center h-[460px]">
          <AudioVisualizer
            status={voiceWs.status}
            mediaStream={audioPipeline.mediaStream}
            onClick={handleConnect}
          />
        </div>
      ) : (
        <ConversationPanel
          messages={allMessages}
          isConnected={false}
          onSendMessage={handleSendMessage}
          isSending={sendMessage.isPending}
          streamingText={streamingText}
          isStreaming={isStreaming}
          useStreaming={useStreaming}
          onToggleStreaming={setUseStreaming}
          topToolbar={
            <VoiceControls
              status={voiceWs.status}
              isMuted={audioPipeline.isMuted}
              selectedUser={selectedUser}
              hasConversation={Boolean(existingConversation?.conversationId)}
              isDeletingConversation={deleteConversation.isPending}
              onConnect={handleConnect}
              onDisconnect={handleDisconnect}
              onToggleMute={audioPipeline.toggleMute}
              onDeleteConversation={handleDeleteConversation}
            />
          }
        />
      )}
    </div>
  );
};

export default VoicePanel;
